import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LEGAL_VERSION } from "@/lib/legal";
import { useAuth } from "@/context/AuthContext";
import PublicThemeControl from "@/components/PublicThemeControl";
import BrandLockup from "@/components/BrandLockup";

export default function LegalConsentPage() {
  const { user, loading, refreshUser } = useAuth();
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [professional, setProfessional] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  if (loading) return <div className="legal-consent-loading" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.legal_version === LEGAL_VERSION) return <Navigate to="/dashboard" replace />;

  const accept = async (event) => {
    event.preventDefault();
    if (!privacy || !terms || !professional) return toast.error("All confirmations are required");
    setSaving(true);
    try {
      await api.post("/account/legal-acceptance", {
        legal_version: LEGAL_VERSION,
        privacy_notice_acknowledged: privacy,
        beta_terms_accepted: terms,
        age_professional_confirmed: professional,
      });
      await refreshUser();
      toast.success("Preferences saved");
      nav("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save your acceptance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="legal-consent-page">
      <header className="legal-consent-header">
        <span className="legal-consent-brand">
          <BrandLockup className="brand-lockup--legal" />
        </span>
        <PublicThemeControl compact />
      </header>

      <main className="legal-consent-shell">
        <section className="legal-consent-card">
          <span className="legal-consent-card__accent" aria-hidden="true" />
          <div className="legal-consent-icon" aria-hidden="true">
            <ShieldCheck size={28} weight="duotone" />
          </div>
          <div className="brand-eyebrow">Private beta</div>
          <h1>Review the current notices</h1>
          <p className="legal-consent-description">
            Callsect&apos;s Privacy Notice and Private Beta Terms have been updated to reflect AssemblyAI
            transcription, the current audio-deletion flow and provider retention. Review and confirm them
            before continuing. Your acceptance time and document version will be recorded.
          </p>

          <div className="segmented-divider" />

          <form onSubmit={accept} className="legal-consent-form">
            <ConsentCheck checked={privacy} onChange={setPrivacy}>
              I have read and acknowledge the{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Notice</Link>.
            </ConsentCheck>
            <ConsentCheck checked={terms} onChange={setTerms}>
              I accept the{" "}
              <Link to="/terms" target="_blank" rel="noreferrer">Private Beta Terms</Link>.
            </ConsentCheck>
            <ConsentCheck checked={professional} onChange={setProfessional}>
              I confirm that I am at least 18 and am using Callsect for professional evaluation.
            </ConsentCheck>

            <button
              type="submit"
              disabled={saving || !privacy || !terms || !professional}
              className="legal-consent-button"
            >
              <CheckCircle size={18} weight="fill" />
              {saving ? "Saving..." : "Accept and continue"}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}

function ConsentCheck({ checked, onChange, children }) {
  return (
    <label className={`legal-consent-check${checked ? " is-checked" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="legal-consent-check__box" aria-hidden="true">
        {checked && <CheckCircle size={17} weight="fill" />}
      </span>
      <span>{children}</span>
    </label>
  );
}
